import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { useAllEnums } from '~/data/enums';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { AppDispatch } from '~/store';
import { isValidPhoneNumber } from '~/utils/helper';

import {
  Country,
  EmpHomeContactType,
  deleteEmpHomeContactData,
  putEmpHomeContactData,
} from './data';

export type IFormInput = {
  fEmpPhoneHome: string | undefined;
  fEmpEmailHome: string;
  fEmpStreetAddress1?: string;
  fEmpStreetAddress2: string;
  fEmpPostalCode?: string;
  fEmpState: string;
  fEmpCity?: string;
  fEmpCountry?: string;
};

export type useHomeContractOutput = {
  handleDelete: (e: React.MouseEvent<HTMLButtonElement>) => void;
  readMode: boolean;
  setReadMode: (data: boolean) => void;
  empData: EmpHomeContactType;
  onSubmit: () => void;
  register: UseFormRegister<IFormInput>;
  errors: FieldErrors<IFormInput>;
  countriesList: Country[];
  control: Control<IFormInput>;
  handleFormReset: (totalReset: boolean) => void;
  isLoading: boolean;
};

export const useHomeContract = (): useHomeContractOutput => {
  const [readMode, setReadMode] = useState(true);
  const {
    employee: { empHomeContact: empData, empBasicData },
    isLoading,
  } = useEmployee();
  const confirm = useConfirmDialog();
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { allEnums } = useAllEnums();
  const countriesList = allEnums['COUNTRY'];

  const validationSchema = React.useMemo(() => {
    return yup.object().shape<IFormInput>({
      fEmpPhoneHome: yup
        .string()
        .test('phoneNumberValidation', t('phoneInvalid'), isValidPhoneNumber),
      fEmpEmailHome: yup.string(),
      fEmpStreetAddress1: yup.string().required(t('laFillManFields')),
      fEmpStreetAddress2: yup.string(),
      fEmpPostalCode: yup.string().required(t('laFillManFields')),
      fEmpState: yup.string(),
      fEmpCity: yup.string().required(t('laFillManFields')),
      fEmpCountry: yup.string().required(t('laFillManFields')),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm<IFormInput>({
    resolver,
  });

  const handleFormReset = (totalReset: boolean) => {
    if (totalReset) {
      reset({
        fEmpPhoneHome: '',
        fEmpEmailHome: '',
        fEmpStreetAddress1: '',
        fEmpStreetAddress2: '',
        fEmpPostalCode: '',
        fEmpState: '',
        fEmpCity: '',
        fEmpCountry: '',
      });
    } else {
      reset();
    }
    setReadMode(true);
  };

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpPhoneHome: data.fEmpPhoneHome,
        fEmpEmailHome: data.fEmpEmailHome,
        fEmpStreetAddress1: data.fEmpStreetAddress1,
        fEmpStreetAddress2: data.fEmpStreetAddress2,
        fEmpPostalCode: data.fEmpPostalCode,
        fEmpState: data.fEmpState,
        fEmpCity: data.fEmpCity,
        fEmpCountry: data.fEmpCountry,
      };
      (async () => {
        try {
          await putEmpHomeContactData(json, empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, empBasicData.fEmpId, enqueueSnackbar, handleSubmit, t]);

  const onSubmit = () => {
    handleFormSubmit();
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    confirm(t('contactDeleteConfirmation'), async (confirmed) => {
      if (confirmed) {
        try {
          await deleteEmpHomeContactData(empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          handleFormReset(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      }
    });
  };
  return {
    control,
    handleDelete,
    countriesList,
    readMode,
    setReadMode,
    empData,
    errors,
    register,
    onSubmit,
    handleFormReset,
    isLoading,
  };
};
